import posthog from 'posthog-js'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface Props {
  copyLink: (linkUrl: string) => Promise<void>
  shareData: (data: ShareData) => Promise<void>
  browserShareType: BrowserShareType
}

export enum BrowserShareType {
  Native = 'native',
  Copy = 'copy',
}

export function useSharePage(): Props {
  const [browserShareType, setBrowserShareType] = useState<BrowserShareType>(
    BrowserShareType.Copy,
  )
  const shareData = useCallback(async (data: ShareData) => {
    if (!navigator?.canShare(data)) {
      posthog.capture('Cannot share data', {
        level: 'warning',
        extra: { data },
      })
      return
    }
    try {
      await navigator.share(data)
    } catch (error: unknown) {
      // ignore errors when user cancels share (e.g. user closes the share dialog)
      if ((error as { message?: string })?.message?.includes('canceled')) return
      console.warn('Error sharing data', error)
    }
  }, [])

  const copyLink = useCallback(async (url: string) => {
    if (!navigator?.clipboard) {
      posthog.capture('Cannot share data', { level: 'warning', extra: { url } })
      return
    }
    await navigator.clipboard.writeText(url)
  }, [])

  useEffect(() => {
    if (typeof navigator?.canShare !== 'undefined') {
      setBrowserShareType(BrowserShareType.Native)
    }
  }, [])

  return useMemo(
    () => ({
      copyLink,
      shareData,
      browserShareType,
    }),
    [copyLink, shareData, browserShareType],
  )
}
